<template>
  <div class="tg-page-root min-h-screen bg-gradient-to-br from-violet-50 via-white to-violet-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
    <!-- Header -->
    <div
      class="mobile-frame-fixed-top fixed top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 shadow-sm"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <button
          type="button"
          aria-label="back"
          @click="goBack"
          class="flex items-center justify-center w-10 h-10 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-lg font-bold text-gray-800 dark:text-gray-100 flex-1 text-left truncate">{{ $t('Payment_Tip_Title') }}</h1>
        <div class="w-10 flex-shrink-0"></div>
      </div>
    </div>
    <div class="fixed-header-spacer" style="--fixed-header-spacer-height: 72px; height: 72px;"></div>

    <div class="px-4 py-6 space-y-5">
      <!-- Amount Card -->
      <div v-if="pendingPayment?.amountUsd" class="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 text-center">
        <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand/10 mb-4">
          <svg class="w-7 h-7 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="text-sm text-gray-500 dark:text-gray-300 mb-1">
          {{ isStarsPayment() ? $t('Payment_Stars_Amount_Label') : $t('Payment_Amount_Label') }}
        </div>
        <div v-if="isStarsPayment() && pendingPayment?.starsAmount" class="text-4xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
          {{ $t('Payment_Stars_Amount_Format', { stars: pendingPayment.starsAmount }) }}
        </div>
        <div v-else-if="isAixPayment() && pendingPayment?.amountRub" class="text-4xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
          {{ $t('Payment_Rub_Amount_Format', { amount: pendingPayment.amountRub }) }}
        </div>
        <div v-else class="text-4xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">${{ pendingPayment.amountUsd }}</div>
        <div v-if="pendingPayment?.productName" class="text-xs text-gray-400 dark:text-gray-300 mt-2">{{ pendingPayment.productName }}</div>
      </div>

      <!-- Order Info Card -->
      <div class="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-brand flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span class="text-gray-800 dark:text-gray-100 font-semibold text-sm">{{ $t('Payment_Order_Title') }}</span>
        </div>

        <!-- Instruction -->
        <p class="text-gray-500 dark:text-gray-300 text-sm leading-relaxed mb-4">
          {{
            isStarsPayment()
              ? $t('Payment_Stars_Instruction', { minutes: pendingPayment?.expiredInMinutes ?? 30 })
              : isAixPayment()
                ? $t('Payment_Aix_Instruction', { minutes: pendingPayment?.expiredInMinutes ?? 30 })
                : $t('Payment_Default_Instruction', { minutes: pendingPayment?.expiredInMinutes ?? 30 })
          }}
        </p>

        <!-- Order Details -->
        <div v-if="pendingPayment?.orderNo" class="flex items-center justify-between py-3 border-t border-gray-100 dark:border-gray-800">
          <span class="text-xs text-gray-400 dark:text-gray-300">{{ $t('Payment_Order_No_Label') }}</span>
          <span class="text-xs text-gray-600 dark:text-gray-300 font-mono">{{ pendingPayment.orderNo }}</span>
        </div>
      </div>

      <!-- Pay Button -->
      <div class="pt-2">
        <button
          class="w-full bg-gradient-to-r from-brand to-brand/90 text-white py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-brand/30 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
          type="button"
          :disabled="!pendingPayment?.orderNo"
          @click="handlePay"
        >
          <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
          <span class="relative z-10 flex items-center justify-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {{ $t('Payment_Go_Pay') }}
          </span>
        </button>
      </div>

      <!-- Polling Status -->
      <div v-if="polling" class="flex items-center justify-center gap-2 py-3">
        <div class="polling-dots flex gap-1">
          <span class="w-1.5 h-1.5 rounded-full bg-brand/60"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-brand/60"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-brand/60"></span>
        </div>
        <span class="text-sm text-gray-400 dark:text-gray-300">{{ $t('Payment_Checking') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import { showAppNotification } from '@/utils/appNotification'
import { useI18n } from 'vue-i18n'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'
import { showAppAlert } from '@/utils/telegramDialog'
import { getPendingPayment, removePendingPayment } from '@/utils/pendingPaymentStorage'
import { claimPaymentResultDialog } from '@/utils/paymentResultGuard'
import {
  PAYMENT_METHOD_STARS,
  PAYMENT_METHOD_COBO,
  executeOrderPayment,
  getAixWayCode,
  isAixPaymentMethod,
  pickPaymentMethod,
} from '@/utils/orderPaymentFlow'
import logger from '@/utils/logger'

const router = useRouter()
const api = inject('api')
const { t, locale } = useI18n()
const {
  bindTelegramEvent,
  unbindTelegramEvent,
  enableClosingConfirmation,
  disableClosingConfirmation,
} = useTelegramWebApp()

const isStarsPayment = () => pendingPayment.value?.paymentMethod === PAYMENT_METHOD_STARS
const isAixPayment = () => isAixPaymentMethod(pendingPayment.value?.paymentMethod)

const pendingPayment = ref(null)
const polling = ref(false)
const lastCheckTime = ref(0)
const isSuccessHandled = ref(false)
let pollTimer = null
let pollCount = 0
let statusCheckInFlight = false

const goBack = () => {
  router.push('/')
}

const loadPendingPayment = async () => {
  const stored = await getPendingPayment()
  if (!stored) {
    pendingPayment.value = null
    return false
  }
  pendingPayment.value = stored
  return true
}

const handlePay = async () => {
  const orderNo = pendingPayment.value?.orderNo
  if (!orderNo) {
    showAppNotification({
      message: t('Payment_Invalid_Link'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  const paymentMethod = await pickPaymentMethod({ t, api, locale: locale.value })
  if (!paymentMethod) {
    return
  }

  const payResult = await executeOrderPayment({
    api,
    router,
    orderNo,
    productName: pendingPayment.value?.productName || '',
    amountUsd: pendingPayment.value?.amountUsd,
    paymentMethod,
    t,
    existingCoboPaymentUrl: (
      paymentMethod === PAYMENT_METHOD_COBO
      && pendingPayment.value?.paymentMethod === PAYMENT_METHOD_COBO
    ) ? pendingPayment.value?.paymentLink : null,
    existingAixPaymentUrl: (
      isAixPaymentMethod(paymentMethod)
      && isAixPaymentMethod(pendingPayment.value?.paymentMethod)
      && getAixWayCode(paymentMethod) === pendingPayment.value?.wayCode
    ) ? pendingPayment.value?.paymentLink : null,
  })

  await loadPendingPayment()

  if (!payResult.ok) {
    showAppNotification({
      message: payResult.message,
      type: 'error',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  if (
    payResult.method === PAYMENT_METHOD_STARS
    && (payResult.invoiceStatus === 'cancelled' || payResult.invoiceStatus === 'failed')
  ) {
    showAppNotification({
      message: t('Payment_Failed_Message'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  startPolling()
}

/**
 * 渐进式退避轮询：前3次每5秒，之后每10秒
 */
const getNextInterval = () => {
  if (pollCount < 2) return 8000    // 前2次：8秒
  if (pollCount < 5) return 15000   // 3-5次：15秒
  return 30000                      // 之后：30秒
}

const scheduleNextPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer)
  }
  pollTimer = setTimeout(async () => {
    await checkStatus()
    pollCount++
    // 仍在轮询则继续
    if (polling.value) {
      scheduleNextPoll()
    }
  }, getNextInterval())
}

const startPolling = () => {
  if (polling.value || !pendingPayment.value?.orderNo) {
    return
  }
  polling.value = true
  pollCount = 0
  checkStatus()
  pollCount++
  scheduleNextPoll()
}

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  polling.value = false
  pollCount = 0
}

/**
 * 页面可见性变化处理
 * 当用户从外部支付页返回时，立即检查一次状态
 */
const handleVisibilityChange = () => {
  if (document.visibilityState !== 'visible' || !pendingPayment.value?.orderNo || isSuccessHandled.value) {
    return
  }

  if (!polling.value) {
    startPolling()
    return
  }

  const now = Date.now()
  if (now - lastCheckTime.value > 3000) {
    checkStatus()
  }
}

/**
 * Telegram WebApp focus 事件与 visibilitychange 可能同时触发，统一走可见性逻辑。
 */
const handleTelegramResume = () => {
  handleVisibilityChange()
}

const finishPaidOrder = async (orderNo) => {
  if (isSuccessHandled.value) return false
  if (!claimPaymentResultDialog(orderNo, 'success')) {
    isSuccessHandled.value = true
    stopPolling()
    await removePendingPayment()
    router.push('/orders')
    return false
  }

  isSuccessHandled.value = true
  stopPolling()
  await removePendingPayment()

  await showAppAlert(
    t('Payment_Success_Message'),
    t('Payment_Success_Title'),
    {
      confirmButtonText: t('Payment_View_Order'),
      type: 'success',
      closeOnClickModal: false,
    },
  )
  router.push('/orders')
  return true
}

const finishFailedOrder = async () => {
  if (isSuccessHandled.value) return
  isSuccessHandled.value = true
  stopPolling()
  await removePendingPayment()

  await showAppAlert(
    t('Payment_Failed_Message'),
    t('Payment_Failed_Title'),
    {
      confirmButtonText: t('Payment_Acknowledge'),
      type: 'warning',
      closeOnClickModal: false,
    },
  )
  router.push('/')
}

const checkStatus = async () => {
  if (isSuccessHandled.value || statusCheckInFlight) return

  const orderNo = pendingPayment.value?.orderNo
  if (!orderNo) {
    stopPolling()
    return
  }

  statusCheckInFlight = true
  try {
    lastCheckTime.value = Date.now()

    const res = await api.order.getOrderStatus(orderNo, { skipLoading: true })
    const data = res?.data?.data
    const statusCode = data?.StatusCode

    if (statusCode === undefined || statusCode === null || statusCode === 0) {
      return
    }

    if (isSuccessHandled.value) return

    if (statusCode === 1 || statusCode === 3) {
      await finishPaidOrder(orderNo)
      return
    }

    if (statusCode === 2) {
      await finishFailedOrder()
      return
    }

    stopPolling()
    await showAppAlert(
      t('Payment_Result_Check'),
      t('Payment_Result_Title'),
      {
        confirmButtonText: t('Payment_Acknowledge'),
        type: 'info',
        closeOnClickModal: false,
      },
    )
    router.push('/')
  } catch (error) {
    logger.error('查询订单状态失败:', error)
  } finally {
    statusCheckInFlight = false
  }
}

const syncClosingConfirmation = () => {
  if (pendingPayment.value?.orderNo) {
    enableClosingConfirmation()
    return
  }
  disableClosingConfirmation()
}

onMounted(async () => {
  const hasPending = await loadPendingPayment()
  syncClosingConfirmation()
  
  // 如果有待支付订单，自动开始轮询
  if (hasPending && pendingPayment.value?.orderNo) {
    startPolling()
  }
  
  // 监听页面可见性变化（用户从外部返回时触发）
  document.addEventListener('visibilitychange', handleVisibilityChange)
  
  // 监听 Telegram WebApp 的 focus 事件
  bindTelegramEvent('viewportChanged', handleTelegramResume)
  bindTelegramEvent('activated', handleTelegramResume)
})

onUnmounted(() => {
  stopPolling()
  disableClosingConfirmation()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  unbindTelegramEvent('viewportChanged', handleTelegramResume)
  unbindTelegramEvent('activated', handleTelegramResume)
})
</script>

<style scoped>
/* Polling dots animation */
.polling-dots span {
  animation: pollingBounce 1.4s ease-in-out infinite;
}
.polling-dots span:nth-child(1) { animation-delay: 0s; }
.polling-dots span:nth-child(2) { animation-delay: 0.2s; }
.polling-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes pollingBounce {
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
